/**
 * with-deadline.ts -- bound one async attempt (network I/O + body read) by a
 * hard deadline.
 *
 * Why it exists: a request the network STALLS (DPI throttling, a dead HTTP/2
 * connection, a captive portal) never rejects, so a `try/catch` around it
 * never fires and any retry ladder above it never climbs. The deadline turns
 * the stall into a rejection.
 *
 * Mechanics:
 *   - AbortController + setTimeout, not AbortSignal.timeout(): the latter is
 *     absent on iOS Safari < 16 / Android WebView < 103.
 *   - The deadline is RACED against the work as well as signalled to it. A
 *     fetch stub / polyfill / middlebox that ignores `signal` would otherwise
 *     keep the promise pending forever, which is the bug being fixed.
 *   - The timer is cleared on every path (success, throw, deadline).
 *   - On deadline the rejection is queued BEFORE abort() so the abort-induced
 *     AbortError from the work never wins the race and the caller always sees
 *     a DeadlineError. The late rejection is still observed by the race, so it
 *     cannot surface as an unhandled rejection.
 */

/** Thrown by withDeadline() when the deadline wins. `name` is 'TimeoutError'. */
export class DeadlineError extends Error {
	constructor(public readonly deadlineMs: number) {
		super(`deadline exceeded after ${deadlineMs} ms`);
		this.name = 'TimeoutError';
	}
}

export async function withDeadline<T>(
	ms: number,
	run: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
	const controller = new AbortController();
	let timer: ReturnType<typeof setTimeout> | undefined;
	const deadline = new Promise<never>((_, reject) => {
		timer = setTimeout(() => {
			reject(new DeadlineError(ms));
			controller.abort();
		}, ms);
	});
	try {
		return await Promise.race([run(controller.signal), deadline]);
	} finally {
		clearTimeout(timer);
	}
}
